import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setUser } from "../../store/common/authSlice";
import {
  fetchPhotoRecolourUsage,
  generateStandaloneRecolour,
  getStandaloneGeneratedBlob,
  getStandaloneOriginalFile,
  stageStandaloneOriginal,
} from "../../store/common/photorealisticScanSlice";
import { useToast } from "../../components/utilities/Toast";
import {
  canAccessPhotoRecolour,
  canUsePhotoRecolourWithoutCredits,
} from "../../utils/employeeRoles";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
];

const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="h-3.5 w-3.5 shrink-0"
    aria-hidden
  >
    <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
    <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
  </svg>
);

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ImagePane: React.FC<{
  label: string;
  accent: "original" | "generated";
  src?: string | null;
  fileName?: string | null;
  generating?: boolean;
  downloading?: boolean;
  onDownload?: () => void;
  emptyTitle: string;
  emptyHint: string;
  footer?: React.ReactNode;
}> = ({
  label,
  accent,
  src,
  fileName,
  generating = false,
  downloading = false,
  onDownload,
  emptyTitle,
  emptyHint,
  footer,
}) => {
  const paneClass =
    accent === "generated"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-[#DDDDD8] bg-[#F8F8F6]";

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2 px-0.5">
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
            accent === "generated"
              ? "bg-emerald-100 text-emerald-800"
              : "bg-gray-200 text-gray-700"
          }`}
        >
          {label}
        </span>
        {src && onDownload && (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1 rounded-md bg-[#eef9fc] px-2.5 py-1 text-xs font-medium text-[#2fa9bf] hover:bg-[#d9f2f7] disabled:opacity-50"
          >
            {downloading ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#2fa9bf]/30 border-t-[#2fa9bf]" />
            ) : (
              <DownloadIcon />
            )}
            {downloading ? "Downloading" : "Download"}
          </button>
        )}
      </div>

      <div
        className={`relative flex h-[42vh] min-h-[260px] max-h-[420px] items-center justify-center overflow-hidden rounded-xl border ${paneClass}`}
      >
        {src ? (
          <>
            <img
              src={src}
              alt={fileName || label}
              className="max-h-full max-w-full object-contain p-3"
            />
            {fileName && (
              <span className="absolute bottom-2 left-2 right-2 truncate rounded-md bg-black/55 px-2 py-1 text-[11px] text-white">
                {fileName}
              </span>
            )}
          </>
        ) : (
          <div className="px-6 text-center">
            <p className="text-sm font-medium text-gray-600">{emptyTitle}</p>
            <p className="mt-1 text-xs text-gray-400">{emptyHint}</p>
          </div>
        )}

        {generating && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/80">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#DDDDD8] border-t-[#2fa9bf]" />
            <p className="text-sm font-medium text-[#2fa9bf]">
              Generating realistic…
            </p>
          </div>
        )}
      </div>

      {footer}
    </div>
  );
};

const PhotoRecolourStudio: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const {
    standaloneLoading,
    standaloneError,
    standaloneSession,
    usage,
    usageLoading,
  } = useAppSelector((state) => state.photorealisticScan);

  const hasAccess = canAccessPhotoRecolour(
    user?.role,
    employee?.role,
    user?.verifyPhotoRecolour,
  );
  const waiveCredits = canUsePhotoRecolourWithoutCredits(
    user?.role,
    employee?.role,
  );
  const recolourCredits = user?.photo_recolour_credits ?? 0;
  const canGenerate = hasAccess && (waiveCredits || recolourCredits > 0);

  useEffect(() => {
    if (!waiveCredits) return;
    void dispatch(fetchPhotoRecolourUsage());
  }, [dispatch, waiveCredits, employee?.emp_id]);

  const usageLabel =
    usage?.scope === "employee"
      ? usage.emp_name || employee?.emp_name || "You"
      : "Super Admin";
  const usageCount = usage?.usageCount ?? 0;

  const originalUrl = standaloneSession.originalUrl;
  const originalName = standaloneSession.originalName;
  const generatedUrl = standaloneSession.generatedUrl;
  const generatedName = standaloneSession.generatedName;
  const file = getStandaloneOriginalFile();
  const generatedBlob = getStandaloneGeneratedBlob();

  const [isDragging, setIsDragging] = useState(false);
  const [downloading, setDownloading] = useState<"original" | "generated" | "both" | null>(null);

  const applyFile = useCallback(
    (next: File) => {
      const looksLikeImage =
        ACCEPTED_TYPES.includes(next.type) ||
        next.type.startsWith("image/") ||
        /\.(jpe?g|png|webp|gif|avif)$/i.test(next.name);

      if (!looksLikeImage) {
        showToast("Please upload a JPEG, PNG, WebP, GIF, or AVIF image.", "error");
        return;
      }

      dispatch(stageStandaloneOriginal(next));
    },
    [dispatch, showToast]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0];
    if (next) applyFile(next);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const next = e.dataTransfer.files?.[0];
    if (next) applyFile(next);
  };

  const handleGenerate = async () => {
    const sourceFile = getStandaloneOriginalFile();
    if (!sourceFile || !user?.user_id || !canGenerate) return;

    try {
      const result = await dispatch(
        generateStandaloneRecolour({
          file: sourceFile,
          user_id: Number(user.user_id),
        })
      ).unwrap();

      if (result.remainingCredits != null) {
        dispatch(
          setUser({
            ...user,
            photo_recolour_credits: result.remainingCredits,
          })
        );
      }

      showToast("Realistic image generated", "success");
    } catch (err: unknown) {
      showToast(
        typeof err === "string" ? err : "Realistic image generation failed",
        "error"
      );
    }
  };

  const handleDownloadOriginal = async () => {
    if (!file) return;
    setDownloading("original");
    try {
      downloadBlob(file, file.name);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadGenerated = async () => {
    if (!generatedBlob || !generatedName) return;
    setDownloading("generated");
    try {
      downloadBlob(generatedBlob, generatedName);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadBoth = async () => {
    if (!file || !generatedBlob || !generatedName) return;
    setDownloading("both");
    try {
      downloadBlob(file, file.name);
      downloadBlob(generatedBlob, generatedName);
    } finally {
      setDownloading(null);
    }
  };

  if (!hasAccess) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-[#E8E8E4] bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2fa9bf]">
            Upcoming
          </p>
          <h1 className="mt-1 text-xl font-semibold text-[#4A4A48]">
            Photo Recolour
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#8A8A85]">
            Photo Recolour restores natural tooth shade, gingival tone, and
            surface characterisation on intraoral photographs, supporting
            accurate shade communication, chairside case presentation, and
            informed patient approval in clinical dental workflows.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#2fa9bf]">
            Independent service
          </p>
          <h1 className="text-2xl font-bold text-[#4A4A48]">Photo Recolour</h1>
          <p className="mt-1 max-w-xl text-sm text-[#8A8A85]">
            Upload a scan or photo, generate a realistic recolour, and download
            the original and generated images. No case is required.
          </p>
        </div>

        {waiveCredits ? (
          <div className="flex items-center gap-3 rounded-xl border border-[#DDDDD8] bg-white px-4 py-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#8A8A85]">
                {usage?.scope === "employee"
                  ? `${usageLabel}'s generations`
                  : "Your generations"}
              </p>
              <p className="text-xl font-bold leading-tight text-[#2fa9bf]">
                {usageLoading && usage == null ? "…" : usageCount}
              </p>
              {usage?.scope === "employee" && usage.emp_role ? (
                <p className="mt-0.5 text-[11px] text-[#8A8A85]">
                  {usage.emp_role}
                  {usage.emp_name ? ` · ${usage.emp_name}` : ""}
                </p>
              ) : (
                <p className="mt-0.5 text-[11px] text-[#8A8A85]">Unlimited</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-[#DDDDD8] bg-white px-4 py-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-[#8A8A85]">
                Credits
              </p>
              <p
                className={`text-xl font-bold leading-tight ${
                  recolourCredits <= 0 ? "text-red-600" : "text-[#2fa9bf]"
                }`}
              >
                {recolourCredits}
              </p>
            </div>
            <Link
              to="/dashboard/photo-recolour/packages"
              className="rounded-lg border border-[#2fa9bf] bg-[#eef9fc] px-3 py-1.5 text-xs font-medium text-[#2fa9bf] hover:bg-[#d9f2f7]"
            >
              Buy credits
            </Link>
          </div>
        )}
      </div>

      {standaloneError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {standaloneError}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {!originalUrl ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex w-full max-w-3xl min-h-[320px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed bg-white transition ${
            isDragging
              ? "border-[#2fa9bf] bg-[#eef9fc]"
              : "border-[#DDDDD8] hover:border-[#2fa9bf]"
          }`}
        >
          <p className="text-sm font-medium text-gray-700">
            Drop an image here or click to upload
          </p>
          <p className="mt-1 text-xs text-gray-400">
            JPEG, PNG, WebP, GIF, or AVIF
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-[#DDDDD8] bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#DDDDD8] bg-[#F8F8F6] px-4 py-3">
              <h3 className="truncate font-bold text-gray-800">
                {originalName}
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {generatedBlob && (
                  <button
                    type="button"
                    onClick={() => void handleDownloadBoth()}
                    disabled={downloading === "both"}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#2fa9bf] bg-white px-3 py-1.5 text-xs font-medium text-[#2fa9bf] hover:bg-[#eef9fc] disabled:opacity-50"
                  >
                    <DownloadIcon />
                    Download both
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-lg border border-[#DDDDD8] bg-white px-3 py-1.5 text-xs font-medium text-[#4A4A48] hover:bg-[#F8F8F6]"
                >
                  Replace image
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
              <ImagePane
                label="Original"
                accent="original"
                src={originalUrl}
                fileName={originalName}
                downloading={downloading === "original" || downloading === "both"}
                onDownload={() => void handleDownloadOriginal()}
                emptyTitle="No original image"
                emptyHint="Upload a photo to start."
              />

              <div className="hidden h-[42vh] max-h-[420px] items-center lg:flex">
                <span className="rounded-full border border-[#DDDDD8] bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  VS
                </span>
              </div>

              <ImagePane
                label="Generated"
                accent="generated"
                src={generatedUrl}
                fileName={generatedName}
                generating={standaloneLoading}
                downloading={downloading === "generated" || downloading === "both"}
                onDownload={
                  generatedBlob
                    ? () => void handleDownloadGenerated()
                    : undefined
                }
                emptyTitle="No realistic version yet"
                emptyHint={
                  canGenerate
                    ? "Generate a realistic recolour from the original on the left."
                    : "Buy credits to generate a realistic version."
                }
                footer={
                  <div className="flex flex-col gap-2">
                    {canGenerate ? (
                      <button
                        type="button"
                        onClick={() => void handleGenerate()}
                        disabled={standaloneLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#2fa9bf] bg-[#eef9fc] px-3 py-2 text-sm font-medium text-[#2fa9bf] transition hover:bg-[#d9f2f7] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {standaloneLoading ? (
                          <>
                            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#2fa9bf]/30 border-t-[#2fa9bf]" />
                            Generating…
                          </>
                        ) : generatedUrl ? (
                          "Generate another"
                        ) : (
                          "Generate realistic"
                        )}
                      </button>
                    ) : (
                      <Link
                        to="/dashboard/photo-recolour/packages"
                        className="inline-flex items-center justify-center rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
                      >
                        Buy Photo Recolour credits
                      </Link>
                    )}
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoRecolourStudio;
